import { createPortal } from "react-dom";
import { ReactComponent as ModalLogo } from "./../assets/svgs/modal-logo.svg";
import { GradientButton } from "./HomeProfilePopupFlow";

const RootPersonalizationModal = ({
  isOpen,
  variant = "guest",
  onClose,
  onSignIn,
  onUnlockMatch,
}) => {
  if (!isOpen) return null;

  const isGuest = variant === "guest";

  const handlePrimaryClick = () => {
    if (isGuest) {
      onSignIn?.();
      return;
    }

    onUnlockMatch?.();
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center overflow-hidden bg-black/55 px-4 py-6"
      style={{ zIndex: 2147483647 }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="root-personalization-title"
        className={`relative flex max-h-[calc(100vh-48px)] w-full flex-col rounded-[6px] bg-white shadow-xl ${
          isGuest ? "max-w-[440px]" : "max-w-[500px]"
        }`}
      >
        <button
          type="button"
          aria-label="Close modal"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full text-[18px] text-[#7B7B7B] transition hover:bg-black/5 hover:text-black"
        >
          ×
        </button>

        <div
          className={`mx-auto flex min-h-0 w-full flex-1 flex-col items-center overflow-y-auto text-center ${
            isGuest
              ? "px-6 pb-6 pt-5 sm:px-8 sm:pt-4"
              : "px-6 pb-7 pt-7 sm:px-10"
          }`}
        >
        <div className="flex flex-col items-center gap-[10px] mt-3">
          <button className="flex h-[52px] w-[50px] items-center justify-center rounded-[5px] bg-gradient-to-br from-[#F5C5B8] via-[#E1A39B] to-[#A84447] shadow-sm transition hover:opacity-90 cursor-default">              
            <ModalLogo />
          </button>
          <h2
            id="root-personalization-title"
            className="m-0 font-inter text-[24px] font-semibold leading-[1.15] text-black sm:text-[28px]"
          >
            This is built around you
          </h2>
        </div>

          <p
            className={`mb-0 font-inter text-[14px] font-normal leading-[1.35] text-[#8A8580] ${
              isGuest ? "mt-3 max-w-[260px]" : "mt-4 max-w-[360px]"
            }`}
          >
            {isGuest
              ? "Get personalized match scores, fit recommendations, and styling advice tailored to your body and preferences."
              : "Products are ranked based on your body, fit, and style."}
          </p>

          <div
            className={`mt-3 flex shrink-0 items-center justify-center rounded-[2px] bg-[#F7F7F7] text-center font-inter text-[10px] text-[#B8B8B8] ${
              isGuest
                ? "h-[210px] w-[214px] sm:h-[220px]"
                : "h-[280px] w-[220px] sm:h-[315px] sm:w-[242px]"
            }`}
          >
            Product Image
          </div>

          <div
            className={`mt-2 grid w-full shrink-0 items-center gap-3 ${
              isGuest
                ? "max-w-[258px] grid-cols-[1fr_auto]"
                : "max-w-[242px] grid-cols-[1fr_auto]"
            }`}
          >
            <p className="m-0 text-left font-spaceMono text-[10px] leading-[1.45] text-[#5B7AB5]">
              match score = body + <br />
              proportions + styling logic
            </p>

            <span className="h-[34px] rounded-[5px] bg-[#E5EBF5] px-3 py-[10px] font-spaceMono text-[10px] font-medium text-[#5B7AB5]">
              95% match
            </span>
          </div>

          {isGuest ? (
            <div className="mt-3 w-full max-w-[220px] shrink-0">
              <button
                type="button"
                onClick={onSignIn}
                className="flex h-[40px] w-full items-center justify-center gap-3 rounded-[5px] border border-[#D9D5D0] bg-white font-inter text-[12px] font-medium text-black transition hover:bg-[#F8F8F8]"
              >
                <span className="text-[13px] font-semibold text-[#4285F4]">
                  G
                </span>
                Continue with Google
              </button>

              <div className="my-3 flex items-center gap-3">
                <span className="h-px flex-1 bg-[#EBE8E5]" />
                <span className="font-inter text-[10px] text-[#8A8580]">
                  or
                </span>
                <span className="h-px flex-1 bg-[#EBE8E5]" />
              </div>

              <button
                type="button"
                onClick={onSignIn}
                className="flex h-[40px] w-full items-center justify-center rounded-[5px] bg-[#252525] font-inter text-[12px] font-normal text-white transition hover:bg-black"
              >
                Continue with Email or Phone
              </button>

              <p className="mx-auto mb-0 mt-3 max-w-[190px] font-inter text-[12px] font-normal leading-[1.35] text-[#8A8580]">
                By continuing, you agree to our Terms of Service and Privacy
                Policy.
              </p>
            </div>
          ) : (
            <div className="mt-3 w-full max-w-[325px] shrink-0">
              <div className="space-y-2 text-left">
                {[
                  "Analyze your body measurements and features",
                  "Rank every piece by how well it fits and flatters you",
                  "Virtual Try On / Personal Styling Assistant",
                ].map((item, index) => (
                  <div key={item} className="flex gap-3">
                    <span className="font-spaceMono text-[12px] text-[#B59963]">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <p className="m-[2px] font-inter text-[12px] leading-[1.35] text-[#3D3A37]">
                      {item}
                    </p>
                  </div>
                ))}
              </div>

              <GradientButton className="mt-4 text-center mx-auto" onClick={handlePrimaryClick}>
                Unlock My Match %
              </GradientButton>

              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full font-inter text-[10px] font-normal text-[#7E7E7E] transition hover:text-black"
              >
                Browse Without Personalization
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default RootPersonalizationModal;
